// components/exercises/lesson/IntervalIdentification.tsx
import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Button, Text } from "@chakra-ui/react";
import DynamicPiano, {
  DynamicPianoHandle,
} from "components/exercises/DynamicPiano";
import { ExerciseDefinition } from "utils/exercises/types";
import { useSound } from "providers/SoundProvider";
import PlayButton from "components/PlayButton";

interface IntervalIdentificationProps {
  exercise: ExerciseDefinition;
  onResult: (isCorrect: boolean) => void;
}

const IntervalIdentification = forwardRef(
  (props: IntervalIdentificationProps, ref) => {
    const { exercise, onResult } = props;

    const pianoRef = useRef<DynamicPianoHandle>(null);
    const { playNote, isAudioPlaying, setIsAudioPlaying } = useSound();
    const [interactionAllowed, setInteractionAllowed] = useState(true); // State to manage interaction with the piano

    const playInterval = async () => {
      if (!isAudioPlaying) {
        const { notes } = exercise.data;
        if (notes && notes.length === 2) {
          setIsAudioPlaying(true);

          playNote(notes[0]);
          await new Promise((resolve) => setTimeout(resolve, 500)); // Delay between notes
          playNote(notes[1]);
        }

        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsAudioPlaying(false);
      }
    };

    useEffect(() => {
      setInteractionAllowed(true);
      if (!isAudioPlaying) {
        playInterval();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exercise.data.notes]); // Re-run when notes change

    useImperativeHandle(ref, () => ({
      checkAnswer: () => localCheckAnswer(),
    }));

    const localCheckAnswer = () => {
      if (pianoRef.current) {
        const userSelectedNotes = pianoRef.current.getUserSelectedNotes();
        const getLocalNotes = exercise.data.notes || [];
        const sortedUserNotes = [...userSelectedNotes].sort((a, b) => a - b); // Clone before sorting to prevent mutations
        const sortedCorrectNotes = [...getLocalNotes].sort((a, b) => a - b);

        const isCorrect =
          userSelectedNotes.length === sortedCorrectNotes.length &&
          sortedUserNotes.every(
            (note, index) => note === sortedCorrectNotes[index]
          );
        onResult(isCorrect); // Communicate the result back to the parent component
        setInteractionAllowed(false); // Disable interaction after checking the answer
      }
    };

    return (
      <Box textAlign="center" width={"100%"}>
        <Text fontSize="xl" fontWeight={500} mb="4">
          Listen to the interval and select the two notes on the piano
        </Text>
        <PlayButton label="Play Interval" onClick={playInterval} />
        <Box mb={6}></Box>

        <DynamicPiano
          ref={pianoRef}
          exerciseDefinition={exercise}
          disabled={!interactionAllowed}
          correctNotes={exercise.data.notes || []}
        />
      </Box>
    );
  }
);

export default IntervalIdentification;
